import { useState } from "react";
import { Phone, Mail, MessageCircle, Check, Loader2, ArrowRight, User, MessageSquare } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { trackWhatsAppClick, trackCallClick, trackEmailClick } from "@/lib/analytics";
import WhatsAppIcon from "@/components/WhatsAppIcon";

interface Props {
  propertyTitle?: string;
  propertyId?: string;
}

const EMAIL = "sercom.construction@gmail.com";

export default function ContactSection({ propertyTitle, propertyId }: Props) {
  const [form, setForm] = useState({ nombre: "", telefono: "", email: "", mensaje: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const whatsappUrl = (msg: string) =>
    `https://wa.me/34644100551?text=${encodeURIComponent(msg)}`;

  const defaultInterestMsg = `Hola, os escribo a través de la web de Sercom. He visto la propiedad${propertyTitle ? ` "${propertyTitle}"` : ""} y me gustaría recibir más información o concertar una visita. ¡Muchas gracias!`;

  const emailSubject = propertyTitle ? `Interés por ${propertyTitle}` : "Interés por una casa";
  const emailBody = `Hola,\n\nHe visto ${propertyTitle ? `la propiedad "${propertyTitle}"` : "una de vuestras casas"} en la web de Sercom y me gustaría recibir más información o, si fuera posible, concertar una visita.\n\nQuedo a la espera de vuestra respuesta.\n\nUn saludo.`;
  const emailHref = `mailto:${EMAIL}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    const { error: insertError } = await supabase.from("contact_requests").insert({
      nombre: form.nombre.trim(),
      telefono: form.telefono.trim() || null,
      email: form.email.trim() || null,
      mensaje: form.mensaje.trim() || null,
      canal: "whatsapp",
      origen: propertyTitle ? `Propiedad: ${propertyTitle}` : "Formulario propiedad",
    });
    setSending(false);
    if (insertError) {
      setError("No hemos podido enviar tu mensaje. Escríbenos por WhatsApp o inténtalo de nuevo.");
      return;
    }
    trackEmailClick(propertyId);
    setSent(true);
    setForm({ nombre: "", telefono: "", email: "", mensaje: "" });
  };

  const fieldClass =
    "h-auto w-full rounded-none border-0 bg-transparent p-0 py-2 text-sm placeholder:text-muted-foreground/70 focus-visible:ring-0 focus-visible:bg-transparent";






  return (
    <div className="glass-surface apple-shadow rounded-2xl p-6 lg:p-7">
      <div className="mb-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent">Contacto directo</p>
        <h3 className="mt-1.5 text-xl font-bold tracking-tight">¿Te interesa esta propiedad?</h3>
        <p className="mt-1 text-sm text-muted-foreground">Te respondemos personalmente y sin compromiso.</p>
      </div>

      <a
        href={whatsappUrl(defaultInterestMsg)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackWhatsAppClick(propertyId)}
        className="block"
      >
        <Button className="w-full gap-2.5 rounded-xl py-5 text-base font-semibold shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl active:scale-[0.98]">
          <WhatsAppIcon className="h-8 w-8" />
          WhatsApp
        </Button>
      </a>

      <div className="mt-3 grid grid-cols-2 gap-2.5">
        <a href="tel:+34644100551" onClick={() => trackCallClick(propertyId)} className="block">
          <Button
            variant="outline"
            className="w-full gap-2 rounded-xl border-white/10 bg-card/50 py-5 text-sm font-semibold text-foreground backdrop-blur-sm transition-colors hover:bg-card hover:text-foreground"
          >
            <Phone className="h-4 w-4" />
            Llamar
          </Button>
        </a>
        <a href={emailHref} onClick={() => trackEmailClick(propertyId)} className="block">
          <Button
            variant="outline"
            className="w-full gap-2 rounded-xl border-white/10 bg-card/50 py-5 text-sm font-semibold text-foreground backdrop-blur-sm transition-colors hover:bg-card hover:text-foreground"
          >
            <Mail className="h-4 w-4" />
            Email
          </Button>
        </a>
      </div>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-white/10" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-3 text-muted-foreground">o completa el formulario</span>
        </div>
      </div>

      {sent ? (
        <div className="flex min-h-[220px] flex-col items-center justify-center text-center">
          <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 text-accent">
            <Check className="h-6 w-6" />
          </span>
          <h4 className="text-lg font-bold">Mensaje recibido</h4>
          <p className="mt-1.5 max-w-xs text-sm text-muted-foreground">
            Gracias. Te contactamos lo antes posible.
          </p>
          <button
            onClick={() => setSent(false)}
            className="mt-5 text-sm font-medium text-accent hover:underline"
          >
            Enviar otro mensaje
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="group flex items-center gap-2 border-b border-foreground/15 px-0 py-1 transition-colors focus-within:border-foreground/40 hover:border-foreground/25">
            <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground/80 transition-colors group-focus-within:text-foreground" />
            <Input
              aria-label="Tu nombre"
              placeholder="Tu nombre"
              required
              maxLength={120}
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div className="group flex items-center gap-2 border-b border-foreground/15 px-0 py-1 transition-colors focus-within:border-foreground/40 hover:border-foreground/25">
            <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground/80 transition-colors group-focus-within:text-foreground" />
            <Input
              aria-label="Teléfono"
              placeholder="Teléfono"
              type="tel"
              required
              maxLength={40}
              value={form.telefono}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div className="group flex items-center gap-2 border-b border-foreground/15 px-0 py-1 transition-colors focus-within:border-foreground/40 hover:border-foreground/25">
            <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground/80 transition-colors group-focus-within:text-foreground" />
            <Input
              aria-label="Email"
              placeholder="Email"
              type="email"
              maxLength={160}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={fieldClass}
            />
          </div>
          <div className="group flex items-start gap-2 border-b border-foreground/15 px-0 py-1 transition-colors focus-within:border-foreground/40 hover:border-foreground/25">
            <MessageSquare className="mt-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/80 transition-colors group-focus-within:text-foreground" />
            <Textarea
              aria-label="Mensaje"
              placeholder="Escribe tu mensaje..."
              maxLength={2000}
              value={form.mensaje}
              onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
              className={`${fieldClass} resize-none`}
              rows={3}
            />
          </div>


          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button
            type="submit"
            disabled={sending}
            className="w-full gap-2.5 rounded-xl py-5 text-base font-semibold shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl active:scale-[0.98]"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {sending ? "Enviando..." : "Enviar"}
          </Button>
        </form>

      )}

      <p className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
        <MessageCircle className="h-3 w-3" />
        Respuesta habitual en menos de 1 hora
      </p>
    </div>
  );
}
